import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  message,
} from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  HomeOutlined,
  LockOutlined,
  MailOutlined,
  WindowsOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import AuthShell from "components/auth/AuthShell";
import AuthProcessingOverlay from "components/auth/AuthProcessingOverlay";
import paths from "routes/paths";
import {
  assertAppleRedirectUri,
  getAppleClientId,
  getAppleRedirectUri,
} from "utils/appleAuth";
import { initiateLinkedInWebSignIn } from "../api/authApi";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { PUBLIC_SITE_URL } from "constants";

const { Title, Text, Paragraph } = Typography;

const primaryButtonStyle = {
  borderRadius: 999,
  height: 48,
  fontWeight: 900,
  fontSize: 15,
  background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen} 0%, ${OLIVEEB_COLORS.greenDark} 100%)`,
  borderColor: OLIVEEB_COLORS.oliveebGreen,
  boxShadow: "0 12px 24px rgba(16, 147, 87, 0.22)",
};

const glossyInputStyle = {
  height: 46,
  borderRadius: 999,
  borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
  background: OLIVEEB_COLORS.white,
  boxShadow: "none",
  fontSize: 15,
  fontWeight: 600,
};

const glossyFormStyle = `
  .olreadia-landing-logo-stage {
    position: relative;
    display: block;
    align-items: center;
    justify-content: center;
    width: min(100%, 600px);
    aspect-ratio: 1672 / 941;
    border-radius: 28px;
    background:
      linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(246,255,246,0.92) 100%);
    border: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
    box-shadow:
      0 24px 56px rgba(39, 174, 48, 0.26),
      0 10px 24px rgba(35, 133, 219, 0.12),
      inset 0 1px 0 rgba(255,255,255,0.95);
    overflow: hidden;
  }
  .olreadia-landing-logo-stage::before {
    content: "";
    position: absolute;
    top: -42px;
    left: -54px;
    width: 150px;
    height: 150px;
    border-radius: 999px;
    background: rgba(255,255,255,0.64);
    transform: rotate(18deg);
  }
  .olreadia-landing-logo-stage::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.78) 38%, transparent 58%);
    transform: translateX(-72%) skewX(-18deg);
    animation: olreadiaLogoShine 5.8s ease-in-out infinite;
  }
  .olreadia-landing-logo-stage img {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: drop-shadow(0 12px 18px rgba(6,105,2,0.14));
  }
  .olreadia-landing-brand-glow {
    position: absolute;
    top: -18px;
    left: -58px;
    width: 285px;
    height: 155px;
    border-radius: 999px;
    background:
      radial-gradient(circle at 35% 44%, rgba(231,246,239,0.98) 0%, rgba(231,246,239,0.92) 42%, rgba(231,246,239,0.18) 74%, transparent 100%);
    opacity: 1;
    filter: blur(1px);
  }
  .olreadia-landing-brand-glow-gold {
    position: absolute;
    top: -2px;
    right: -72px;
    width: 255px;
    height: 135px;
    border-radius: 999px;
    background:
      radial-gradient(circle at 42% 48%, rgba(255,248,219,0.98) 0%, rgba(255,248,219,0.90) 45%, rgba(248,193,11,0.14) 75%, transparent 100%);
    opacity: 0.96;
    filter: blur(1px);
  }
  @keyframes olreadiaLogoShine {
    0%, 58% { transform: translateX(-76%) skewX(-18deg); opacity: 0; }
    68% { opacity: 0.9; }
    84%, 100% { transform: translateX(82%) skewX(-18deg); opacity: 0; }
  }
  .olreadia-login-form .ant-form-item-label > label {
    color: ${OLIVEEB_COLORS.oliveebGreen};
    font-size: 13px;
    font-weight: 900;
  }
  .olreadia-login-form .ant-input,
  .olreadia-login-form .ant-input-affix-wrapper {
    border-radius: 999px !important;
    font-size: 15px;
    background: ${OLIVEEB_COLORS.white} !important;
    box-shadow: none !important;
  }
  .olreadia-login-form .ant-form-item-control-input-content {
    filter: drop-shadow(0 8px 14px rgba(16,147,87,0.10));
  }
  .olreadia-login-form .ant-input-affix-wrapper-focused,
  .olreadia-login-form .ant-input-affix-wrapper:focus {
    border-color: ${OLIVEEB_COLORS.oliveebGreen} !important;
    box-shadow: 0 0 0 4px rgba(16, 147, 87, 0.12) !important;
  }

  @media (max-width: 640px) {
    .olreadia-landing-logo-stage {
      width: 100%;
      border-radius: 18px;
      box-shadow:
        0 16px 34px rgba(39, 174, 48, 0.18),
        0 8px 18px rgba(35, 133, 219, 0.10);
    }

    .olreadia-landing-brand-glow,
    .olreadia-landing-brand-glow-gold {
      display: none;
    }

    .olreadia-login-form .ant-form-item {
      margin-bottom: 14px;
    }
  }
`;

const AppleBrandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const FacebookBrandIcon = () => (
  <span style={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}>f</span>
);

const LinkedInBrandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "822618703980688";
const MICROSOFT_CLIENT_ID =
  process.env.REACT_APP_MICROSOFT_CLIENT_ID || "366258d5-f9f5-4591-81eb-22976b18775f";
const GOOGLE_SDK_SRC = "https://accounts.google.com/gsi/client";
const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const MICROSOFT_SDK_SRC =
  "https://cdn.jsdelivr.net/npm/@azure/msal-browser@2.38.3/lib/msal-browser.min.js";
const MICROSOFT_SDK_SRC_FALLBACK =
  "https://unpkg.com/@azure/msal-browser@2.38.3/lib/msal-browser.min.js";

let googleSdkPromise = null;
let facebookSdkPromise = null;

const getGoogleInitClientId = () => {
  if (typeof window === "undefined") return null;
  return window.__projectMgtGoogleInitializedClientId || null;
};

const setGoogleInitClientId = (clientId) => {
  if (typeof window === "undefined") return;
  window.__projectMgtGoogleInitializedClientId = clientId;
};

const loadGoogleSdk = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google sign-in is only available in the browser."));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (!googleSdkPromise) {
    googleSdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${GOOGLE_SDK_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener(
          "error",
          () => {
            googleSdkPromise = null;
            reject(new Error("Failed to load Google sign-in."));
          },
          { once: true },
        );
        return;
      }

      const script = document.createElement("script");
      script.src = GOOGLE_SDK_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        googleSdkPromise = null;
        reject(new Error("Failed to load Google sign-in."));
      };
      document.body.appendChild(script);
    });
  }

  return googleSdkPromise;
};

const loadFacebookSdk = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Facebook sign-in is only available in the browser."));
  }

  if (window.FB) return Promise.resolve();

  if (!facebookSdkPromise) {
    facebookSdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${FACEBOOK_SDK_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load Facebook sign-in.")), { once: true });
        if (existing.dataset.loaded === "true") resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = FACEBOOK_SDK_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
      };
      script.onerror = () => {
        facebookSdkPromise = null;
        reject(new Error("Failed to load Facebook sign-in."));
      };
      document.body.appendChild(script);
    }).then(() => {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: "v19.0",
      });
    });
  }

  return facebookSdkPromise;
};

const loadMicrosoftSdk = (useFallback = false) => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Microsoft sign-in is only available in the browser."));
  }

  if (window.msal?.PublicClientApplication) {
    return Promise.resolve();
  }

  const scriptSrc = useFallback ? MICROSOFT_SDK_SRC_FALLBACK : MICROSOFT_SDK_SRC;
  const sourceLabel = useFallback ? "fallback" : "primary";

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => {
        let attempts = 0;
        const maxAttempts = 50;
        const interval = setInterval(() => {
          if (window.msal?.PublicClientApplication) {
            clearInterval(interval);
            resolve();
          } else if (++attempts >= maxAttempts) {
            clearInterval(interval);
            if (!useFallback) {
              loadMicrosoftSdk(true).then(resolve).catch(reject);
            } else {
              reject(new Error(`Microsoft SDK (${sourceLabel}) loaded but msal.PublicClientApplication not available.`));
            }
          }
        }, 100);
      }, 100);
    };
    script.onerror = () => {
      if (!useFallback) {
        loadMicrosoftSdk(true).then(resolve).catch(reject);
      } else {
        reject(new Error(`Failed to load Microsoft SDK from ${sourceLabel} source.`));
      }
    };
    document.body.appendChild(script);
  });
};

const GoogleSignInButton = ({ onSuccess, onError, disabled }) => {
  const containerRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    let rendered = false;
    let buttonNode = null;

    const renderButton = async () => {
      try {
        if (!GOOGLE_CLIENT_ID) {
          throw new Error("Google sign-in is not configured.");
        }

        await loadGoogleSdk();
        buttonNode = containerRef.current;
        if (cancelled || !buttonNode || !window.google?.accounts?.id) {
          return;
        }

        const initializedClientId = getGoogleInitClientId();
        if (initializedClientId !== GOOGLE_CLIENT_ID) {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: (response) => {
              if (response?.credential) {
                onSuccessRef.current?.(response.credential);
              } else {
                onErrorRef.current?.(new Error("No Google credential returned"));
              }
            },
          });
          setGoogleInitClientId(GOOGLE_CLIENT_ID);
        }

        if (cancelled || !buttonNode) return;
        buttonNode.innerHTML = "";
        window.google.accounts.id.renderButton(buttonNode, {
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "pill",
          width: 300,
          logo_alignment: "left",
        });
        rendered = true;
      } catch (err) {
        onErrorRef.current?.(err);
      }
    };

    renderButton();

    return () => {
      cancelled = true;
      if (rendered && buttonNode) {
        buttonNode.innerHTML = "";
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: 300,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
      aria-disabled={disabled}
    />
  );
};

const APPLE_CLIENT_ID = getAppleClientId();
const APPLE_REDIRECT_URI = assertAppleRedirectUri(getAppleRedirectUri());
const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

let appleSdkPromise = null;

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return {};

  try {
    const [, payloadPart] = token.split(".");
    if (!payloadPart) return {};
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
};

const buildAppleAuthPayload = (appleResponse = {}) => {
  const authorization = appleResponse?.authorization || {};
  const identityToken =
    authorization.id_token ||
    authorization.idToken ||
    appleResponse?.identityToken ||
    appleResponse?.id_token ||
    appleResponse?.idToken ||
    "";
  const authorizationCode = authorization.code || appleResponse?.code || "";
  const jwtPayload = decodeJwtPayload(identityToken);
  const appleUser = appleResponse?.user;
  const appleUserId =
    (typeof appleUser === "string" ? appleUser : "") ||
    appleResponse?.userId ||
    jwtPayload.sub ||
    "";
  const appleProfile = typeof appleUser === "object" && appleUser ? appleUser : {};
  const appleName = appleProfile.name || {};
  const firstName = appleName.firstName || appleName.givenName || jwtPayload.given_name || "";
  const lastName = appleName.lastName || appleName.familyName || jwtPayload.family_name || "";
  const email = appleProfile.email || appleResponse?.email || jwtPayload.email || "";

  return {
    authPayload: {
      identityToken,
      idToken: identityToken,
      authorizationCode,
      code: authorizationCode,
      user: appleUserId,
      appleUserId,
      providerUserId: appleUserId,
      fullName:
        firstName || lastName
          ? {
              givenName: firstName,
              familyName: lastName,
            }
          : undefined,
      email,
      redirectUri: APPLE_REDIRECT_URI,
    },
    profile: {
      email,
      firstName,
      lastName,
    },
  };
};

const loadAppleSdk = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Apple sign-in is only available in the browser."));
  }

  if (window.AppleID?.auth) {
    return Promise.resolve();
  }

  if (!appleSdkPromise) {
    appleSdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${APPLE_SDK_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => {
          appleSdkPromise = null;
          reject(new Error("Failed to load Apple sign-in."));
        }, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = APPLE_SDK_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        appleSdkPromise = null;
        reject(new Error("Failed to load Apple sign-in."));
      };
      document.body.appendChild(script);
    });
  }

  return appleSdkPromise;
};

const isAppleSignInCancelledError = (err) => {
  const code = String(err?.code || err?.errorCode || "").toUpperCase();
  const message = String(err?.message || err?.error || "").toLowerCase();

  return (
    code === "1000" ||
    code === "ERR_CANCELED" ||
    code === "ABORT_ERR" ||
    message.includes("cancel") ||
    message.includes("canceled") ||
    message.includes("cancelled") ||
    message.includes("aborted") ||
    message.includes("user cancelled") ||
    message.includes("user canceled") ||
    message.includes("authorization error 1000")
  );
};

  const AppleSignInButton = ({ onSuccess, onError, loading: disabled }) => {
    const handleAppleSignIn = async () => {
      try {
        await loadAppleSdk();

        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true,
          state:
            (window.crypto && typeof window.crypto.randomUUID === "function"
              ? window.crypto.randomUUID()
              : `apple-${Date.now()}-${Math.random().toString(36).slice(2)}`),
        });

        const data = await window.AppleID.auth.signIn();
        const authorization = data?.authorization || {};
        const code = authorization.code || data?.code || null;
        const idToken =
          authorization.id_token || authorization.idToken || data?.idToken || null;

        if (code || idToken) {
          onSuccess({ code, idToken, raw: data });
        } else {
          throw new Error("No authorization code or id token returned");
        }
      } catch (err) {
        if (isAppleSignInCancelledError(err)) {
          return;
        }
        onError(err);
      }
    };

    return (
      <button
        type="button"
        onClick={handleAppleSignIn}
        disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        maxWidth: 300,
        height: 42,
        borderRadius: 24,
        border: `1px solid ${OLIVEEB_COLORS.ink}`,
        background: OLIVEEB_COLORS.ink,
        color: OLIVEEB_COLORS.white,
        fontSize: 14,
        fontWeight: 600,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'opacity .15s, transform .15s',
        opacity: disabled ? 0.6 : 1,
        padding: '0 20px',
        outline: 'none',
      }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.02)'; } }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <AppleBrandIcon />
      Sign in with Apple
    </button>
  );
};

const AuthLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    isAuthenticated,
    loginWithGoogle,
    loginWithAppleCode,
    loginWithApple,
    loginWithSocialProvider,
  } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(
    location.pathname === paths.simpleSessionExpired,
  );
  const processingLabel = useMemo(() => {
    if (googleLoading) return "Authenticating with Google...";
    if (microsoftLoading) return "Authenticating with Microsoft...";
    if (facebookLoading) return "Authenticating with Facebook...";
    if (appleLoading) return "Authenticating with Apple...";
    if (linkedInLoading) return "Redirecting to LinkedIn...";
    if (loading) return "Signing you in...";
    return "";
  }, [appleLoading, facebookLoading, googleLoading, linkedInLoading, loading, microsoftLoading]);

  useEffect(() => {
    setSessionExpired(location.pathname === paths.simpleSessionExpired);
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.prefillEmail) {
      form.setFieldsValue({ email: location.state.prefillEmail });
    }
  }, [form, location.state?.prefillEmail]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(paths.pmDashboard, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const redirectAfterLogin = () => paths.pmDashboard;

  const canCompleteSocialSignup = (result) => {
    const details = result?.details || {};
    return Boolean(
      details?.requiresPassword ||
      details?.requiresLinkApproval ||
      details?.userInfo ||
      result?.status === 201,
    );
  };

  const beginSocialSignup = (provider, authPayload, details = {}, fallbackProfile = {}) => {
    if (details?.requiresLinkApproval) {
      navigate(paths.socialSetup || paths.appleSetup, {
        state: {
          mode: "link",
          provider,
          authPayload,
          providerUserId:
            details?.providerUserId ||
            details?.googleUserId ||
            details?.appleUserId ||
            details?.microsoftUserId ||
            details?.facebookUserId ||
            details?.linkedinUserId ||
            authPayload?.providerUserId ||
            authPayload?.googleUserId ||
            authPayload?.appleUserId ||
            authPayload?.microsoftUserId ||
            authPayload?.facebookUserId ||
            authPayload?.linkedinUserId ||
            authPayload?.user ||
            fallbackProfile?.providerUserId ||
            fallbackProfile?.googleUserId ||
            fallbackProfile?.appleUserId ||
            fallbackProfile?.microsoftUserId ||
            fallbackProfile?.facebookUserId ||
            fallbackProfile?.linkedinUserId ||
            "",
          userInfo: details?.userInfo || fallbackProfile,
          message:
            details?.message ||
            `An account already exists with this email. Confirm linking to continue with ${provider}.`,
          linkingProcessId: details?.linkingProcessId || "",
          requiresPassword: !!details?.requiresPassword,
          requiresLinkApproval: true,
        },
      });
      return;
    }

    navigate(paths.simpleSignup, {
      state: {
        socialDraft: {
          provider,
          authPayload,
          details,
          prefill: fallbackProfile,
          userInfo: details?.userInfo || fallbackProfile,
        },
      },
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (!result?.success) {
        throw new Error(result?.error || "Unable to sign in.");
      }
      navigate(redirectAfterLogin(), { replace: true });
    } catch (error) {
      message.error(error.message || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    try {
      if (!MICROSOFT_CLIENT_ID) throw new Error("Microsoft sign-in is not configured.");
      await loadMicrosoftSdk();

      const msalInstance = new window.msal.PublicClientApplication({
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          authority: "https://login.microsoftonline.com/common",
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false,
        },
      });
      
      let response;
      try {
        response = await msalInstance.loginPopup({
          scopes: ["openid", "profile", "email"],
          prompt: "select_account",
        });
      } catch (popupErr) {
        const errMsg = String(popupErr?.message || popupErr?.errorMessage || "").toLowerCase();
        if (errMsg.includes("popup") || errMsg.includes("window") || errMsg.includes("blocked")) {
          throw new Error("Microsoft sign-in popup was blocked. Please allow popups for this site and try again.");
        }
        throw popupErr;
      }
      
      console.log("Microsoft login response:", response);
      const idToken = response?.idToken;
      if (!idToken) {
        console.error("Microsoft login response:", JSON.stringify(response, null, 2));
        throw new Error("No Microsoft ID token returned. Check browser console for details.");
      }

      const result = await loginWithSocialProvider("microsoft", { idToken });
      if (!result?.success) {
        if (canCompleteSocialSignup(result)) {
          const account = response?.account || {};
          beginSocialSignup(
            "microsoft",
            { idToken },
            result?.details || {},
            {
              email: account.username || "",
              name: account.name || "",
            },
          );
          return;
        }
        throw new Error(result?.error || "Microsoft sign-in failed.");
      }

      navigate(redirectAfterLogin(), { replace: true });
    } catch (err) {
      const msg = err?.message || err?.errorMessage || "";
      const text = String(msg).toLowerCase();
      if (!text.includes("user_cancelled") && !text.includes("cancel")) {
        message.error(msg || "Microsoft sign-in failed.");
      }
    } finally {
      setMicrosoftLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setFacebookLoading(true);
    try {
      await loadFacebookSdk();
      const facebookResponse = await new Promise((resolve, reject) => {
        window.FB.login(
          (response) => {
            if (response?.authResponse?.accessToken) {
              resolve(response);
            } else {
              reject(new Error("Facebook sign-in was cancelled or did not return a token."));
            }
          },
          { scope: "email,public_profile" },
        );
      });

      const facebookProfile = await new Promise((resolve) => {
        window.FB.api(
          "/me",
          { fields: "id,first_name,last_name,name,email,picture" },
          (profile) => resolve(profile || {}),
        );
      });
      const authPayload = {
        accessToken: facebookResponse.authResponse.accessToken,
        providerUserId: facebookProfile.id || "",
        facebookUserId: facebookProfile.id || "",
      };
      const result = await loginWithSocialProvider("facebook", authPayload);
      if (!result?.success) {
        if (canCompleteSocialSignup(result)) {
          beginSocialSignup("facebook", authPayload, result?.details || {}, {
            email: facebookProfile.email,
            name: facebookProfile.name,
            firstName: facebookProfile.first_name,
            lastName: facebookProfile.last_name,
            picture: facebookProfile.picture?.data?.url,
            providerUserId: facebookProfile.id,
            facebookUserId: facebookProfile.id,
          });
          return;
        }
        throw new Error(result?.error || "Facebook sign-in failed.");
      }

      navigate(redirectAfterLogin(), { replace: true });
    } catch (error) {
      message.error(error?.message || "Facebook sign-in failed.");
    } finally {
      setFacebookLoading(false);
    }
  };

  const handleLinkedInLogin = async () => {
    setLinkedInLoading(true);
    try {
      const callbackUrl = `${window.location.origin}/auth/linkedin/callback`;
      const { redirectUrl } = await initiateLinkedInWebSignIn(callbackUrl);
      if (!redirectUrl) {
        throw new Error("LinkedIn sign-in URL was not returned.");
      }
      window.location.assign(redirectUrl);
    } catch (error) {
      message.error(error?.message || "LinkedIn sign-in failed.");
    } finally {
      setLinkedInLoading(false);
    }
  };

  return (
    <AuthShell mode="split">
      <AuthProcessingOverlay
        visible={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading || linkedInLoading}
        message={processingLabel}
      />
      <style>{glossyFormStyle}</style>
      <Row
        gutter={[24, 24]}
        style={{ width: "100%", maxWidth: 1200 }}
        align="middle"
      >
        <Col xs={24} lg={13}>
          <div
            style={{
              color: OLIVEEB_COLORS.textPrimary,
              padding: "48px 52px",
              maxWidth: 640,
              minHeight: 650,
              borderRadius: 30,
              border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
              background:
                `linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(246,255,246,0.92) 62%, rgba(231,246,239,0.94) 100%), url("/welcome2.png") center bottom / cover`,
              boxShadow: "0 24px 64px rgba(6, 105, 2, 0.12)",
            }}
          >
            <Space
              vertical
              size={0}
              align="start"
              style={{
                width: "100%",
                marginBottom: 70,
              }}
            >
              <div style={{ display: "inline-flex", alignItems: "center", marginBottom: 86 }}>
                <img
                  src="/oliveeb/ocare.png"
                  alt="Olreadia Care"
                  style={{
                    display: "block",
                    height: 82,
                    width: "auto",
                    maxWidth: 300,
                    objectFit: "contain",
                  }}
                />
              </div>
              <Title
                level={1}
                style={{
                  color: OLIVEEB_COLORS.oliveebDarkGreen,
                  margin: 0,
                  fontWeight: 900,
                  fontSize: 38,
                  lineHeight: 1.12,
                  letterSpacing: 0,
                }}
              >
                Care facility operations and care worker tools in one workspace.
              </Title>
            </Space>

            <Paragraph
              style={{
                color: OLIVEEB_COLORS.textMuted,
                fontSize: 17,
                lineHeight: 1.5,
                maxWidth: 520,
                margin: "0 0 28px",
                fontWeight: 500,
              }}
            >
              Sign in to manage residents,{" "}
              <strong className="care-landing-highlight-blue">
                <em>Assessments</em>
              </strong>
              ,{" "}
              <strong className="care-landing-highlight-blue">
                <em>Negotiated Care Plans</em>
              </strong>
              ,{" "}
              <strong className="care-landing-highlight-blue">
                <em>schedules</em>
              </strong>
              ,{" "}
              <strong className="care-landing-highlight-blue">
                <em>attendance</em>
              </strong>
              ,{" "}
              <strong className="care-landing-highlight-blue">
                <em>clocking in &amp; out</em>
              </strong>
              , and{" "}
              <strong className="care-landing-highlight-blue">
                <em>timesheets</em>
              </strong>{" "}
              in one Olreadia Care workspace.
            </Paragraph>

            <Space vertical size={16} style={{ width: "100%" }}>
              {[
                ["ti-clipboard-list", "Assessments and care plans", "Capture state assessments, negotiated care plans, final assessment pages, and resident updates in one place."],
                ["ti-calendar-time", "Schedules, attendance, and timesheets", "Coordinate caregiver schedules, clocking in and out, daily notes, and staff payment support."],
                ["ti-sparkles", "Care documents, websites, and AI", "Manage internal downloads, billing, websites, and AI-assisted care workflows from the same workspace."],
              ].map(([icon, title, body], index) => (
                <div
                  key={title}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "58px 1fr",
                    gap: 14,
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        index === 1
                          ? OLIVEEB_COLORS.oliveebGreen
                          : OLIVEEB_COLORS.oliveebGreenSoft,
                      color:
                        index === 1
                          ? OLIVEEB_COLORS.white
                          : OLIVEEB_COLORS.oliveebGreen,
                      fontSize: 25,
                    }}
                  >
                    <i className={`ti ${icon}`} />
                  </span>
                  <div>
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        color: OLIVEEB_COLORS.oliveebGreen,
                        fontSize: 17,
                        lineHeight: 1.2,
                      }}
                    >
                      {title}
                    </Title>
                    <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 14, lineHeight: 1.45 }}>
                      {body}
                    </Text>
                  </div>
                </div>
              ))}
            </Space>
          </div>
        </Col>

        <Col xs={24} lg={11}>
          <Card
            variant="borderless"
            style={{
              borderRadius: 28,
              boxShadow: "0 24px 70px rgba(0, 64, 56, 0.13)",
              border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
              background:
                `linear-gradient(165deg, ${OLIVEEB_COLORS.white} 0%, ${OLIVEEB_COLORS.greenSoft2} 52%, ${OLIVEEB_COLORS.white} 100%)`,
              backdropFilter: "blur(18px)",
              overflow: "hidden",
            }}
            styles={{ body: { padding: 0 } }}
          >
            <div
              style={{
                padding: 32,
                background: OLIVEEB_COLORS.white,
                display: "flex",
                flexDirection: "column",
                minHeight: 520,
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <Button
                  type="text"
                  icon={<HomeOutlined />}
                  onClick={() => navigate("/")}
                  style={{
                    minWidth: 96,
                    height: 40,
                    padding: "6px 15px",
                    borderRadius: 999,
                    color: OLIVEEB_COLORS.oliveebBlue,
                    border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
                    background:
                      `linear-gradient(135deg, ${OLIVEEB_COLORS.white} 0%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
                    fontSize: 14,
                    fontWeight: 900,
                    boxShadow: "0 10px 20px rgba(35, 133, 219, 0.14)",
                  }}
                >
                  Home
                </Button>
              </div>
              <Space orientation="vertical" size={8} style={{ width: "100%" }}>
                <Text
                  style={{
                    textTransform: "uppercase",
                    letterSpacing: "0.18em",
                    color: OLIVEEB_COLORS.oliveebGreen,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {sessionExpired ? "Session expired" : "Start with Olreadia Care"}
                </Text>
                <Title
                  level={2}
                  style={{
                    margin: 0,
                    color: OLIVEEB_COLORS.oliveebDarkGreen,
                    fontSize: 25,
                    lineHeight: 1.18,
                  }}
                >
                  Sign in to Olreadia Care
                </Title>
                <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 14, lineHeight: 1.45 }}>
                  Use your{" "}
                  <a
                    href={PUBLIC_SITE_URL}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: OLIVEEB_COLORS.oliveebBlue,
                      fontWeight: 800,
                      textDecoration: "none",
                    }}
                  >
                    Oliveeb
                  </a>{" "}
                  credentials or SSO to access residents, assessments, care plans,
                  schedules, attendance, daily notes, documents, billing, websites,
                  and AI-assisted care workflows.
                </Text>
              </Space>

              {sessionExpired && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 20, borderRadius: 14 }}
                  message="Your session expired"
                  description="Please sign in again to continue working where you left off."
                />
              )}

              <Form
                form={form}
                className="olreadia-login-form"
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
                size="large"
                style={{ marginTop: 24, flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <Form.Item
                  label="Email"
                  name="email"
                  style={{ marginBottom: 14 }}
                  rules={[
                    { required: true, message: "Enter your email address" },
                    { type: "email", message: "Enter a valid email address" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    placeholder="you@company.com"
                    autoComplete="email"
                    disabled={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading}
                    style={glossyInputStyle}
                  />
                </Form.Item>

                <Form.Item
                  label="Password"
                  name="password"
                  style={{ marginBottom: 12 }}
                  rules={[
                    { required: true, message: "Enter your password" },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading}
                    style={glossyInputStyle}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 20,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="link"
                    onClick={() => navigate(paths.simpleRecoverRequest)}
                    style={{ paddingLeft: 0 }}
                  >
                    Forgot password?
                  </Button>
               
                </div>

                <div style={{ width: '100%', marginTop: 'auto' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    loading={loading}
                    disabled={googleLoading || microsoftLoading || facebookLoading || appleLoading || linkedInLoading}
                    style={primaryButtonStyle}
                  >
                    Sign in
                  </Button>
                  <Button
                    type="link"
                    block
                    onClick={() => navigate(paths.getStarted, { state: location.state })}
                    style={{ marginTop: 10, fontWeight: 700 }}
                  >
                    New to Olreadia Care? Create an account
                  </Button>
                </div>
              </Form>

              <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <GoogleSignInButton
                  disabled={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading}
                  onSuccess={async (idToken) => {
                    setGoogleLoading(true);
                    try {
                      if (!idToken) throw new Error("No credential returned");
                      const result = await loginWithGoogle(idToken);
                      if (!result?.success) throw new Error(result?.error || "Google sign-in failed");
                      navigate(redirectAfterLogin(), { replace: true });
                    } catch (err) {
                      message.error(err.message || "Google sign-in failed");
                    } finally {
                      setGoogleLoading(false);
                    }
                  }}
                  onError={(err) => message.error(err.message || "Google sign-in failed")}
                />

                <Button
                  block
                  size="large"
                  icon={<WindowsOutlined />}
                  loading={microsoftLoading}
                  disabled={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading || linkedInLoading}
                  onClick={handleMicrosoftLogin}
                  style={{
                    maxWidth: 300,
                    height: 42,
                    borderRadius: 24,
                    fontWeight: 800,
                    color: OLIVEEB_COLORS.oliveebDarkBlue,
                    borderColor: OLIVEEB_COLORS.oliveebBlue,
                    background: OLIVEEB_COLORS.white,
                  }}
                >
                  Continue with Microsoft
                </Button>

                {/*
                <Button
                  block
                  size="large"
                  icon={<FacebookBrandIcon />}
                  loading={facebookLoading}
                  disabled={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading || linkedInLoading}
                  onClick={handleFacebookLogin}
                  style={{
                    maxWidth: 300,
                    height: 42,
                    borderRadius: 24,
                    fontWeight: 800,
                    background: "#1877F2",
                    borderColor: "#1877F2",
                    color: OLIVEEB_COLORS.white,
                  }}
                >
                  Continue with Facebook
                </Button>
                */}

                <Button
                  block
                  size="large"
                  icon={<LinkedInBrandIcon />}
                  loading={linkedInLoading}
                  disabled={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading}
                  onClick={handleLinkedInLogin}
                  style={{
                    maxWidth: 300,
                    height: 42,
                    borderRadius: 24,
                    fontWeight: 800,
                    background: "#0077B5",
                    borderColor: "#0077B5",
                    color: OLIVEEB_COLORS.white,
                  }}
                >
                  Continue with LinkedIn
                </Button>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    maxWidth: 300,
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: OLIVEEB_COLORS.line }} />
                  <span style={{ fontSize: 12, color: OLIVEEB_COLORS.textSecondary, whiteSpace: 'nowrap' }}>
                    or
                  </span>
                  <div style={{ flex: 1, height: 1, background: OLIVEEB_COLORS.line }} />
                </div>

                <AppleSignInButton
                  loading={loading || googleLoading || microsoftLoading || facebookLoading || appleLoading || linkedInLoading}
                  onSuccess={async (response) => {
                    setAppleLoading(true);
                    try {
                      const appleResponse = response?.raw || {};
                      const code = response?.code || appleResponse?.authorization?.code;
                      const idToken =
                        response?.idToken ||
                        appleResponse?.authorization?.id_token ||
                        appleResponse?.authorization?.idToken ||
                        appleResponse?.identityToken ||
                        appleResponse?.id_token;
                      const result = code
                        ? await loginWithAppleCode(appleResponse)
                        : await loginWithApple(appleResponse);
                      if (!result?.success) {
                        const details = result?.details || {};
                        if (details?.requiresPassword || details?.requiresLinkApproval) {
                          const appleUserId =
                            (typeof appleResponse?.user === "string"
                              ? appleResponse.user
                              : "") ||
                            appleResponse?.userId ||
                            details?.appleUserId ||
                            "";

                          navigate(paths.appleSetup, {
                            state: {
                              mode: details?.requiresLinkApproval ? "link" : "setup",
                              appleResponse,
                              identityToken: idToken,
                              appleUserId,
                              userInfo: details?.userInfo || {
                                email: details?.userInfo?.email || "",
                                name: details?.userInfo?.name || "",
                                picture: details?.userInfo?.picture || "",
                              },
                              message:
                                details?.message ||
                                "Apple sign-in requires additional account setup.",
                              linkingProcessId: details?.linkingProcessId || "",
                              requiresPassword: !!details?.requiresPassword,
                              requiresLinkApproval: !!details?.requiresLinkApproval,
                            },
                          });
                          return;
                        }

                        throw new Error(result?.error || "Apple sign-in failed.");
                      }
                      navigate(redirectAfterLogin(), { replace: true });
                    } catch (err) {
                      if (isAppleSignInCancelledError(err)) {
                        return;
                      }
                      message.error(err.message || "Apple sign-in failed");
                    } finally {
                      setAppleLoading(false);
                    }
                  }}
                  onError={(err) => {
                    if (isAppleSignInCancelledError(err)) return;
                    message.error(err.message || "Apple sign-in failed");
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </AuthShell>
  );
};

export default AuthLogin;
